import { Intent } from "@blueprintjs/core";
import { HexType, SpecialVersionType, hexIdToHumanHexId } from "@hex/common";
import React from "react";
import styled, { useTheme } from "styled-components";

import {
  Flex,
  HexAnchorButton,
  HexButton,
  HexTooltip,
} from "../../../hex-components";
import { useTerminology } from "../../../hooks/useTerminology";
import { ORG_ID } from "../../../orgs";
import { Routes } from "../../../route/routes";
import { StatusLabel } from "../../common/labels/StatusLabel";
import { useGetDataConnectionDetailsForGraphNodeQuery } from "../../data/DataConnectionGraphNode.generated";
import { Heading } from "../../Heading";
import {
  ArrowTopRightIcon,
  DataframeIcon,
  EyeOpenIcon,
  ProjectIcon,
  StarActiveIcon,
  WarehouseTableIcon,
} from "../../icons/CustomIcons";

import { RichTextMentionData } from "./RichTextMentionPlugin";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 5px 2px;
  width: 100%;
`;

const SchemaPath = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  color: ${({ theme }) => theme.fontColor.MUTED};
`;

const TitleRow = styled(Heading)`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  line-height: 14px;
  max-width: 100%;
  gap: 4px;
`;

const TableName = styled.span`
  transform: none;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  white-space: normal;
  overflow: hidden;
  overflow-wrap: break-word;
  display: inline-block;
  max-width: 220px;
  min-width: 0;
`;

const HexMentionContainer = styled.div`
  display: flex;
  padding: 3px 0;
  flex-direction: column;
  gap: 2px;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

const HexMentionMeta = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  color: ${({ theme }) => theme.fontColor.DEFAULT};
`;

const HexMentionTitle = styled.span`
  color: ${({ theme }) => theme.fontColor.DEFAULT};
`;

const Description = styled.div`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  color: ${({ theme }) => theme.fontColor.MUTED};
  line-height: ${({ theme }) => theme.lineHeight.SMALL};

  /* TODO(DES-1060): do not pattern match, prefer line-clamp */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ButtonWrapper = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
`;

const Metadata = styled.span`
  display: flex;
  gap: 8px;
  align-items: start;
`;

const Columns = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  color: ${({ theme }) => theme.fontColor.MUTED};
  background-color: ${({ theme }) => theme.backgroundColor.DARK};
  width: calc(100% - 16px);

  /* padding doesn't work properly with line-clamp, so this approximates padding */
  border: 5px solid transparent;

  /* TODO(DES-1060): do not pattern match, prefer line-clamp */
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export function DataframeMentionDetails({
  cellLabel,
  mention,
}: {
  cellLabel?: string;
  mention: RichTextMentionData.Dataframe;
}): React.ReactElement {
  const columnsText =
    mention.columns.length > 0
      ? mention.columns.join(", ")
      : mention.isPlanned
        ? `Run ${cellLabel ?? "cell"} to see columns`
        : "";

  return (
    <Wrapper>
      <TitleRow css="padding-bottom: 4px;" renderAs="h2">
        <DataframeIcon />
        <TableName>{mention.name}</TableName>
      </TitleRow>
      {columnsText && <Columns>{columnsText}</Columns>}
    </Wrapper>
  );
}

export function TableMentionDetails({
  mention,
  regenButton,
  showInDataBrowser,
}: {
  mention: RichTextMentionData.Table;
  showInDataBrowser?: (mention: RichTextMentionData.Table) => void;
  regenButton?: JSX.Element;
}): React.ReactElement | null {
  const { dataBrowserText } = useTerminology();
  const { data: connectionData } = useGetDataConnectionDetailsForGraphNodeQuery(
    {
      variables: { dataConnectionId: mention.connectionId },
    },
  );
  const connection = connectionData?.dataConnectionById;

  return (
    <Wrapper>
      <SchemaPath>
        {[
          connection?.connectionName ?? "",
          mention.databaseName,
          mention.schemaName,
        ]
          .filter((x) => !!x)
          .join(" / ")}
      </SchemaPath>
      <TitleRow renderAs="h2">
        <WarehouseTableIcon />
        <TableName>{mention.tableName}</TableName>
        {mention.pinned && <PinnedTableIcon />}
        <Metadata>
          {mention.status && (
            <StatusLabel minimal={true} status={mention.status} />
          )}
        </Metadata>
      </TitleRow>
      <Description>{mention.description}</Description>

      <ButtonWrapper>
        {regenButton ? (
          <>
            {regenButton}
            {/* mouseDown is important, because it fires before the MagicInput blurs and hides the mention autocomplete */}
            <HexTooltip content={`Show in ${dataBrowserText}`} placement="top">
              <HexButton
                icon={<EyeOpenIcon />}
                minimal={true}
                small={true}
                onMouseDown={() => showInDataBrowser?.(mention)}
              />
            </HexTooltip>
          </>
        ) : (
          <>
            {/* mouseDown is important, because it fires before the MagicInput blurs and hides the mention autocomplete */}
            <HexButton
              small={true}
              onMouseDown={() => showInDataBrowser?.(mention)}
            >
              Show in {dataBrowserText}
            </HexButton>
          </>
        )}
      </ButtonWrapper>
    </Wrapper>
  );
}

export function HexMentionDetails({
  mention,
}: {
  mention: RichTextMentionData.Hex;
}): React.ReactElement {
  const hexLocator = hexIdToHumanHexId(mention.id, mention.title);
  const route =
    mention.hexType === HexType.COMPONENT ? Routes.COMPONENT : Routes.LOGIC;
  const version = SpecialVersionType.DRAFT;

  return (
    <HexMentionContainer>
      <HexMentionMeta>
        <Flex gap={4}>
          <ProjectIcon />
          <HexMentionTitle>
            {mention.title.length > 30
              ? mention.title.substring(0, 30) + "..."
              : mention.title}
          </HexMentionTitle>
        </Flex>
        <HexAnchorButton
          extraSmall={true}
          href={route.href(ORG_ID, true, {
            hexLocator,
            version,
          })}
          intent={Intent.PRIMARY}
          minimal={true}
          rightIcon={<ArrowTopRightIcon />}
          target="_blank"
          text="View"
        />
      </HexMentionMeta>
    </HexMentionContainer>
  );
}

export function PinnedTableIcon() {
  const theme = useTheme();

  return (
    <HexTooltip content="Favorite table">
      <StarActiveIcon color={theme.userActionColor} />
    </HexTooltip>
  );
}
